import { useEffect } from "react";
import {
  GetBookingQuery,
  useProgressBookingMutation,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { useTheme } from "../../../hooks/useTheme";
import styled from "../../../styles";
import { Button, ButtonWrap } from "../../CTA";
import { Flex } from "../../Flex";
import Loader from "../../Loader";
import { Underline } from "../../Tabs";
import { Text } from "../../Text";
import { View } from "../../View";

const StyledFlex = styled(Flex)`
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

interface Props {
  booking: GetBookingQuery["booking"];
  onCancel: () => void;
  onComplete: () => void;
}

export const CompleteBookingOverlay = (props: Props) => {
  const client = useGqlClient();
  const progressBooking = useProgressBookingMutation(client);
  const theme = useTheme();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        props.onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props]);

  return (
    <>
      {progressBooking.isLoading ? (
        <Flex align="center" justify="center">
          <Loader />
        </Flex>
      ) : (
        <StyledFlex>
          <Text
            weight="bold"
            size="xl"
            margin={"0 0 0 0"}
            colorPreset="heading"
          >
            Complete Booking
          </Text>
          <Underline selected />
          <Text margin={"m 0 0 0"} colorPreset="secondary" isInline>
            Are you sure? Not all required content has been posted.{" "}
            <span
              style={{
                fontWeight: theme.typography.weight.semi,
                color: theme.color.typography.text,
              }}
            >
              If you complete this booking, any additional content will not be
              tracked on Joli.
            </span>
          </Text>

          <ButtonWrap margin="xl 0 0">
            <Button buttonType="secondary" onClick={props.onCancel}>
              Cancel
            </Button>
            <Button onClick={props.onComplete}>Complete</Button>
          </ButtonWrap>
        </StyledFlex>
      )}
    </>
  );
};

export const Alert = styled(View)`
  background-color: ${(p) => p.theme.color.secondary}30;
  border-left: 3px solid ${(p) => p.theme.color.secondary};
  border-radius: 2px;
  width: 100%;
  box-sizing: border-box;

  color: ${(p) => p.theme.color.secondary};
`;
